import { useState } from 'react';
import { ArtifactWorkspace, Icon, icons, theme } from '@agentisai/design-system';
import { ArtifactPreview, formatArtifactSize, type DisplayArtifact } from './ArtifactPreview';

/**
 * WorkstreamArtifacts — the persistent deliverables panel (Stage 3c, closing
 * ADR-0041's deferred item). Feeds the DS `ArtifactWorkspace` (tab strip +
 * metadata strip) from the workstream's artifact ledger, so deliverables no
 * longer live inside the composer region and no longer vanish on the next
 * turn. The S3 `key` is the durable identity (ADR-0040) and the tab id.
 *
 * Ledger entries are metadata-only on the wire; `url`/`preview` are enriched
 * client-side from the turns that produced them (HomePage's artifact cache),
 * so an entry may legitimately have neither — the body then says how to get
 * it back instead of rendering a dead card.
 */
export function WorkstreamArtifacts({ artifacts }: { artifacts: readonly DisplayArtifact[] }) {
  // Active tab — component-local (frontend-architecture R1.0): the workstream
  // itself isn't URL-addressable yet, so neither is its active deliverable.
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const active = artifacts.find((a) => a.key === activeKey) ?? artifacts[0];
  if (!active) return null;

  return (
    <ArtifactWorkspace
      tabs={artifacts.map((a) => ({
        id: a.key,
        label: a.filename,
        icon: <Icon as={icons.FileText} size={14} />,
      }))}
      activeId={active.key}
      onSelect={setActiveKey}
      metaLeft={
        <>
          {active.contentType} · {formatArtifactSize(active.sizeBytes)}
        </>
      }
      metaRight={
        active.url && !active.expired ? (
          <a
            href={active.url}
            download={active.filename}
            style={{ color: theme.color.accent.blue[700], font: 'inherit' }}
          >
            Download
          </a>
        ) : active.url ? (
          <span style={{ color: theme.color.neutral.muted }}>
            Download link expired — ask the agent to share it again
          </span>
        ) : null
      }
    >
      {active.preview ? (
        <ArtifactPreview artifact={active} />
      ) : (
        <p style={{ color: theme.color.neutral.muted, margin: 0 }}>
          No preview held for this deliverable — ask the agent to share {active.filename} again.
        </p>
      )}
    </ArtifactWorkspace>
  );
}
WorkstreamArtifacts.displayName = 'WorkstreamArtifacts';
